// frontend/src/App.jsx
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import Layout from './components/Layout';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import LazyFallback from './components/LazyFallback';

// ── Eagerly loaded ─────────────────────────────────────────────────────────
// Every screen used to be imported this way, so one bundle carried all 26 —
// including the Platform superadmin pages a salesperson will never open.
//
// These five stay eager because they are where a session STARTS. Making them
// lazy would add a second network round trip before anything appears: the
// bundle arrives, then the app asks for another file before it can paint. A
// logged-out visitor lands on Login; a logged-in one lands on Dashboard.
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';

// ── Lazily loaded ──────────────────────────────────────────────────────────
// Fetched the first time the route is visited, then cached by the browser for
// the rest of the session. A failed chunk fetch throws during render, which the
// RouteErrorBoundary below already catches — so a network blip shows the
// "could not be displayed" panel rather than a blank screen.
const Users               = lazy(() => import('./pages/Users'));
const Departments         = lazy(() => import('./pages/Departments'));
const Leads               = lazy(() => import('./pages/Leads'));
const SalesPipeline       = lazy(() => import('./pages/SalesPipeline'));
const PipelineCleanup     = lazy(() => import('./pages/PipelineCleanup'));
const Quotations          = lazy(() => import('./pages/Quotations'));
const Vendors             = lazy(() => import('./pages/Vendors'));
const VendorPrices        = lazy(() => import('./pages/VendorPrices'));
const Enquiries           = lazy(() => import('./pages/Enquiries'));
const ProcurementWorklist = lazy(() => import('./pages/ProcurementWorklist'));
const EnquiryCompare      = lazy(() => import('./pages/EnquiryCompare'));
const VendorCatalog       = lazy(() => import('./pages/VendorCatalog'));
const Reports             = lazy(() => import('./pages/Reports'));
const Tasks               = lazy(() => import('./pages/Tasks'));
const FollowUps           = lazy(() => import('./pages/FollowUps'));
const CallLogs            = lazy(() => import('./pages/CallLogs'));
const Settings            = lazy(() => import('./pages/Settings'));
const Branches            = lazy(() => import('./pages/Branches'));
const QuotationSettings   = lazy(() => import('./pages/QuotationSettings'));
const PlatformDashboard   = lazy(() => import('./pages/Platform/PlatformDashboard'));
const PlatformAuditLog    = lazy(() => import('./pages/Platform/PlatformAuditLog'));
const SalesOrders         = lazy(() => import('./pages/SalesOrders'));
const PurchaseOrders      = lazy(() => import('./pages/PurchaseOrders'));
const Invoices            = lazy(() => import('./pages/Invoices'));

// Import your API services
import quotationAPI from './api/quotationAPI';
import leadAPI from './api/leadAPI';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5150/api';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);
        return { success: true };
      } else {
        return { success: false, message: data.message || 'Login failed' };
      }
    } catch (error) {
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  // Used by both login and signup — either flow returns { token, user }.
  const setSession = (data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const handleLogout = () => {
    logout();
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px'
      }}>
        Loading CRM Application...
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {!user ? (
        <Routes>
          <Route path="/login" element={<Login onLogin={login} />} />
          <Route path="/signup" element={<SignUp onSignedUp={setSession} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      ) : (
        <Layout user={user} onLogout={logout}>
          {/* Inside Layout on purpose: a page that throws is contained to the
              content area, and the sidebar stays usable so the user can go
              somewhere else instead of reloading. */}
          <RouteErrorBoundary>
          {/* Suspense sits INSIDE the boundary so a chunk that fails to
              download surfaces as the error panel, not a blank content area. */}
          <Suspense fallback={<LazyFallback />}>
          <Routes>
            {user.role === 'platform_superadmin' ? (
              <>
                <Route path="/" element={<Navigate to="/platform" replace />} />
                <Route path="/platform" element={<PlatformDashboard />} />
                <Route path="/platform/audit-log" element={<PlatformAuditLog />} />
                <Route path="*" element={<Navigate to="/platform" replace />} />
              </>
            ) : (
              <>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={<Dashboard user={user} onLogout={handleLogout} />}
            />
            <Route path="/users" element={<Users />} />
            <Route path="/departments" element={<Departments />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/pipeline" element={<SalesPipeline />} />
            <Route path="/pipeline/cleanup" element={<PipelineCleanup />} />
            <Route path="/tasks" element={<Tasks user={user} />} />
            <Route path="/follow-ups" element={<FollowUps user={user} />} />
            {/* FIXED: Pass the required props to Quotations */}
            <Route 
              path="/quotations" 
              element={
                <Quotations 
                  user={user}
                  quotationAPI={quotationAPI}
                  leadAPI={leadAPI}
                />
              } 
            />
            <Route path="/sales-orders" element={<SalesOrders />} />
            <Route path="/purchase-orders" element={<PurchaseOrders />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/vendors" element={<Vendors />} />
            <Route path="/vendor-prices" element={<VendorPrices />} />
            <Route path="/enquiries"   element={<Enquiries />} />
            <Route path="/procurement" element={<ProcurementWorklist />} />
            <Route path="/vendor-catalog" element={<VendorCatalog />} />
            <Route path="/enquiry/:enquiryNumber" element={<EnquiryCompare />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/settings" element={<Settings user={user} />} />
            <Route path="/settings/quotations" element={<QuotationSettings user={user} />} />
            <Route path="/branches" element={<Branches />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
            <Route path="/call-logs" element={<CallLogs user={user} />} />
              </>
            )}
          </Routes>
          </Suspense>
          </RouteErrorBoundary>
        </Layout>
      )}
    </ThemeProvider>
  );
}

export default App;